import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import {
  HeroSection,
  HowItWorksSection,
  FeaturesSection,
  TestimonialsSection,
  CTASection,
  CreatorMessageSection,
  CreatorDonationsSection,
  FAQSection,
  Footer,
} from '@/sections';
import { BottomNavigation, ToastContainer } from '@/components/ui/custom';
import { PageLoadingFallback } from '@/components/system';
import { useToast } from '@/hooks';
import type { Professional } from '@/types';

const ExplorePage = lazy(() =>
  import('@/pages/ExplorePage').then((m) => ({ default: m.ExplorePage })),
);
const ArticlesPage = lazy(() =>
  import('@/pages/ArticlesPage').then((m) => ({ default: m.ArticlesPage })),
);
const ProfessionalProfilePage = lazy(() =>
  import('@/pages/ProfessionalProfilePage').then((m) => ({ default: m.ProfessionalProfilePage })),
);
const BookingPage = lazy(() =>
  import('@/pages/BookingPage').then((m) => ({ default: m.BookingPage })),
);
const ConfirmationPage = lazy(() =>
  import('@/pages/ConfirmationPage').then((m) => ({ default: m.ConfirmationPage })),
);
const ProfessionalSignupPage = lazy(() =>
  import('@/pages/ProfessionalSignupPage').then((m) => ({ default: m.ProfessionalSignupPage })),
);
const AdminDashboardPage = lazy(() =>
  import('@/pages/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage })),
);

type Page =
  | 'landing'
  | 'explore'
  | 'profile'
  | 'booking'
  | 'confirmation'
  | 'professional-signup'
  | 'admin-dashboard'
  | 'articles';

interface NavigationState {
  page: Page;
  professional: Professional | null;
}

function App() {
  const [currentPage, setCurrentPage] = useState<Page>('landing');
  const [selectedProfessional, setSelectedProfessional] = useState<Professional | null>(null);
  const [navItem, setNavItem] = useState('home');
  const [navigationHistory, setNavigationHistory] = useState<NavigationState[]>([]);

  const { toasts, success, error, removeToast } = useToast();

  useEffect(() => {
    const handlePopState = () => {
      if (navigationHistory.length > 0) {
        const previous = navigationHistory[navigationHistory.length - 1];
        setNavigationHistory((prev) => prev.slice(0, -1));
        setCurrentPage(previous.page);
        setSelectedProfessional(previous.professional);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [navigationHistory]);

  const navigateTo = useCallback(
    (page: Page, professional: Professional | null = null) => {
      setNavigationHistory((prev) => [...prev, { page: currentPage, professional: selectedProfessional }]);

      setCurrentPage(page);
      if (professional) {
        setSelectedProfessional(professional);
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [currentPage, selectedProfessional],
  );

  const handleExploreClick = () => {
    navigateTo('explore');
    setNavItem('explore');
  };

  const handleProfessionalSignupClick = () => {
    navigateTo('professional-signup');
  };

  const handleProfessionalClick = (professional: Professional) => {
    navigateTo('profile', professional);
  };

  const handleBookingComplete = (_data: { date: string; time: string }) => {
    if (!selectedProfessional) return;

    success('Consulta marcada com sucesso!');
    navigateTo('confirmation');
  };

  const handleBack = () => {
    if (navigationHistory.length > 0) {
      const previous = navigationHistory[navigationHistory.length - 1];
      setNavigationHistory((prev) => prev.slice(0, -1));
      setCurrentPage(previous.page);
      setSelectedProfessional(previous.professional);
    } else {
      setCurrentPage('landing');
      setNavItem('home');
      setSelectedProfessional(null);
    }
  };

  const handleNavItemClick = (id: string) => {
    setNavItem(id);
    if (id === 'home') {
      navigateTo('landing');
    } else if (id === 'explore') {
      navigateTo('explore');
    } else if (id === 'articles') {
      navigateTo('articles');
    }
  };

  const handleProfessionalSignupSuccess = () => {
    success('Candidatura submetida com sucesso! Vamos analisar e entrar em contacto.');
    navigateTo('landing');
    setNavItem('home');
  };

  const scrollToSection = (id: string) => {
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleFooterComoFunciona = () => {
    scrollToSection('como-funciona');
  };

  const handleFooterEncontrarProfissionais = () => {
    handleExploreClick();
  };

  const handleFooterSouPsicologo = () => {
    navigateTo('professional-signup');
  };

  const handleFooterPerguntasFrequentes = () => {
    scrollToSection('perguntas-frequentes');
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'landing':
        return (
          <div className="min-h-screen min-h-[100dvh] bg-transparent pb-bottom-nav">
            <HeroSection
              onExploreClick={handleExploreClick}
              onProfessionalSignupClick={handleProfessionalSignupClick}
            />
            <HowItWorksSection />
            <FeaturesSection />
            <TestimonialsSection />
            <CTASection onExploreClick={handleExploreClick} />
            <CreatorDonationsSection />
            <CreatorMessageSection />
            <FAQSection />
            <Footer
              onComoFuncionaClick={handleFooterComoFunciona}
              onEncontrarProfissionaisClick={handleFooterEncontrarProfissionais}
              onSouPsicologoClick={handleFooterSouPsicologo}
              onPerguntasFrequentesClick={handleFooterPerguntasFrequentes}
            />
            <BottomNavigation activeItem={navItem} onItemClick={handleNavItemClick} />
          </div>
        );

      case 'explore':
        return (
          <>
            <ExplorePage
              onBack={handleBack}
              onProfessionalClick={handleProfessionalClick}
              onToast={success}
            />
            <BottomNavigation activeItem={navItem} onItemClick={handleNavItemClick} />
          </>
        );

      case 'articles':
        return (
          <>
            <ArticlesPage onBack={handleBack} />
            <BottomNavigation activeItem={navItem} onItemClick={handleNavItemClick} />
          </>
        );

      case 'profile':
        return selectedProfessional ? (
          <ProfessionalProfilePage
            professional={selectedProfessional}
            onBack={handleBack}
            onToast={success}
          />
        ) : (
          <div className="flex min-h-[100dvh] min-h-screen flex-col items-center justify-center gap-6 px-6">
            <div className="max-w-sm text-center">
              <p className="text-lg font-semibold text-foreground">Profissional não encontrado</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Volta à lista e escolhe um perfil para continuar.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                navigateTo('explore');
                setNavItem('explore');
              }}
              className="btn-kuiva"
            >
              Ver profissionais
            </button>
          </div>
        );

      case 'booking':
        return selectedProfessional ? (
          <BookingPage
            professional={selectedProfessional}
            onBack={handleBack}
            onComplete={handleBookingComplete}
            onToast={{ success, error }}
          />
        ) : (
          <div className="flex min-h-[100dvh] min-h-screen flex-col items-center justify-center gap-6 px-6">
            <p className="max-w-sm text-center text-muted-foreground">
              Não há profissional selecionado para marcar consulta.
            </p>
            <button type="button" onClick={() => navigateTo('explore')} className="btn-kuiva">
              Explorar profissionais
            </button>
          </div>
        );

      case 'confirmation':
        return selectedProfessional ? (
          <ConfirmationPage
            professional={selectedProfessional}
            onHomeClick={() => {
              navigateTo('landing');
              setNavItem('home');
            }}
            onToast={success}
          />
        ) : (
          <div className="flex min-h-[100dvh] min-h-screen flex-col items-center justify-center gap-6 px-6">
            <p className="text-muted-foreground">Sem dados de confirmação.</p>
            <button type="button" onClick={() => navigateTo('landing')} className="btn-kuiva">
              Ir ao início
            </button>
          </div>
        );

      case 'professional-signup':
        return (
          <ProfessionalSignupPage
            onBack={handleBack}
            onSubmitSuccess={handleProfessionalSignupSuccess}
            onToast={{ success, error }}
          />
        );

      case 'admin-dashboard':
        return <AdminDashboardPage onBack={handleBack} onToast={{ success, error }} />;

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] overflow-x-hidden bg-transparent">
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <main
        id="conteudo-principal"
        tabIndex={-1}
        className="outline-none"
        aria-label="Conteúdo principal"
      >
        <Suspense fallback={<PageLoadingFallback />}>{renderPage()}</Suspense>
      </main>
    </div>
  );
}

export default App;
